"use client";

import {
	Camera,
	Check,
	ChevronDown,
	CircleAlert,
	Clock3,
	Disc,
	Download,
	Laptop,
	Mic,
	MonitorPlay,
	MonitorUp,
	Pause,
	Play,
	ShieldCheck,
	StopCircle,
	Volume2,
	VolumeX,
	X,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/utils/ui";
import {
	ScreenRecording,
	RecordingOptions,
	RecordingState,
	AudioActivity,
	formatDuration,
	getFileInfo,
} from "@/media/screen-recording";

type RecordingLayout = "screen" | "screen-camera" | "camera";
type Quality = "720p" | "1080p";
type Countdown = "off" | "3" | "5";
type CameraPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";
type BrowserState = "checking" | "supported" | "unsupported";

const subscribeToBrowserCapability = () => () => {};

function getBrowserCapability(): BrowserState {
	return typeof navigator.mediaDevices?.getDisplayMedia === "function"
		? "supported"
		: "unsupported";
}

const RECORDING_LAYOUTS = [
	{
		value: "screen" as const,
		labelKey: "layout.screen" as const,
		icon: MonitorUp,
	},
	{
		value: "screen-camera" as const,
		labelKey: "layout.screenCamera" as const,
		icon: Laptop,
	},
	{
		value: "camera" as const,
		labelKey: "layout.camera" as const,
		icon: Camera,
	},
] as const;

const CAMERA_POSITIONS = [
	{ value: "top-left" as const, labelKey: "camera.topLeft" as const },
	{ value: "top-right" as const, labelKey: "camera.topRight" as const },
	{ value: "bottom-left" as const, labelKey: "camera.bottomLeft" as const },
	{
		value: "bottom-right" as const,
		labelKey: "camera.bottomRight" as const,
	},
] as const;

function OptionButton<T extends string>({
	value,
	selectedValue,
	onSelect,
	children,
	className,
}: {
	value: T;
	selectedValue: T;
	onSelect: (value: T) => void;
	children: React.ReactNode;
	className?: string;
}) {
	const selected = value === selectedValue;

	return (
		<button
			type="button"
			aria-pressed={selected}
			onClick={() => onSelect(value)}
			className={cn(
				"relative min-h-11 rounded-xl border px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
				selected
					? "border-blue-500 bg-blue-50 text-blue-950 shadow-sm dark:border-cyan-500 dark:bg-cyan-950/45 dark:text-cyan-50"
					: "border-slate-200 bg-white/80 text-slate-700 hover:border-blue-300 hover:bg-blue-50/60 dark:border-slate-800 dark:bg-slate-950/70 dark:text-slate-200 dark:hover:border-cyan-800 dark:hover:bg-cyan-950/25",
				className,
			)}
		>
			{children}
			{selected ? (
				<Check
					className="absolute top-2 right-2 size-3.5 text-blue-600 dark:text-cyan-300"
					aria-hidden="true"
				/>
			) : null}
		</button>
	);
}

export function ScreenRecorder() {
	const t = useTranslations("ScreenRecorder.tool");
	const locale = useLocale();
	const [layout, setLayout] = useState<RecordingLayout>("screen");
	const [microphone, setMicrophone] = useState(true);
	const [systemAudio, setSystemAudio] = useState(true);
	const [quality, setQuality] = useState<Quality>("1080p");
	const [countdown, setCountdown] = useState<Countdown>("3");
	const [cameraPosition, setCameraPosition] =
		useState<CameraPosition>("bottom-right");
	const browserState = useSyncExternalStore(
		subscribeToBrowserCapability,
		getBrowserCapability,
		() => "checking" as const,
	);
	// Recording state
	const [recordingState, setRecordingState] = useState<RecordingState>({
		status: "setup",
		duration: 0,
	});
	const [audioActivity, setAudioActivity] = useState<AudioActivity>({
		microphone: false,
		systemAudio: false,
	});
	const [countdownValue, setCountdownValue] = useState(0);
	const screenRecordingRef = useRef<ScreenRecording | null>(null);

	const hasScreen = layout !== "camera";
	const hasCamera = layout !== "screen";
	const canStart = browserState === "supported" || layout === "camera";

	// Cleanup recording instance on unmount
	useEffect(() => {
		return () => {
			if (screenRecordingRef.current) {
				screenRecordingRef.current.abortRecording();
			}
		};
	}, []);

	const selectLayout = (nextLayout: RecordingLayout) => {
		if (recordingState.status !== "setup") return;
		setLayout(nextLayout);
		if (nextLayout === "camera") setSystemAudio(false);
	};

	const handleStartRecording = async () => {
		if (recordingState.status !== "setup") return;

		const recording = new ScreenRecording();
		screenRecordingRef.current = recording;

		const options: RecordingOptions = {
			layout,
			microphone,
			systemAudio,
			quality,
			countdown,
			cameraPosition,
		};

		await recording.startRecording({
			options,
			onStateChange: (state) => {
				setRecordingState(state);
			},
			onAudioActivity: (activity) => {
				setAudioActivity(activity);
			},
		});
	};

	const handlePauseRecording = () => {
		if (screenRecordingRef.current) {
			screenRecordingRef.current.pauseRecording();
		}
	};

	const handleResumeRecording = () => {
		if (screenRecordingRef.current) {
			screenRecordingRef.current.resumeRecording();
		}
	};

	const handleStopRecording = async () => {
		if (screenRecordingRef.current) {
			setRecordingState((prev) => ({ ...prev, status: "saving" }));
			await screenRecordingRef.current.stopRecording();

			const result = screenRecordingRef.current.getRecording();
			if (result) {
				setRecordingState({
					status: "success",
					duration: recordingState.duration,
					recordedBlob: result.blob,
					recordedUrl: result.url,
				});
			} else {
				setRecordingState({
					status: "failed",
					duration: recordingState.duration,
					error: t("errors.output"),
				});
			}
		}
	};

	const handleDownloadRecording = () => {
		if (recordingState.recordedBlob && recordingState.recordedUrl) {
			const a = document.createElement("a");
			a.href = recordingState.recordedUrl;
			a.download = `screen-recording-${Date.now()}.webm`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		}
	};

	const handleResetRecording = () => {
		if (recordingState.recordedUrl) {
			URL.revokeObjectURL(recordingState.recordedUrl);
		}
		setRecordingState({
			status: "setup",
			duration: 0,
		});
		setAudioActivity({
			microphone: false,
			systemAudio: false,
		});
		setCountdownValue(0);
		screenRecordingRef.current = null;
	};

	const handleCancelRecording = () => {
		if (screenRecordingRef.current) {
			screenRecordingRef.current.abortRecording();
		}
		handleResetRecording();
	};

	const isRecording =
		recordingState.status === "recording" ||
		recordingState.status === "paused" ||
		recordingState.status === "countdown";
	const isSaving = recordingState.status === "saving";
	const isSuccess = recordingState.status === "success";
	const isFailed = recordingState.status === "failed";
	const isSetup = recordingState.status === "setup";
	const recordingError = recordingState.error?.includes("capture screen")
		? t("errors.screenPermission")
		: recordingState.error?.includes("access camera")
			? t("errors.cameraPermission")
			: recordingState.error === t("errors.output")
				? recordingState.error
				: t("errors.generic");

	return (
		<section
			aria-label={t("ariaLabel")}
			className="mx-auto w-full max-w-[84rem]"
		>
			<div className="overflow-hidden rounded-[2rem] border border-blue-200/80 bg-slate-100/92 shadow-[0_30px_90px_-50px_rgba(37,99,235,0.55)] backdrop-blur-xl dark:border-blue-900/75 dark:bg-slate-950/90 dark:shadow-[0_30px_90px_-50px_rgba(34,211,238,0.3)]">
				<div className="flex flex-col gap-3 border-b border-blue-100 bg-white/82 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7 dark:border-blue-950 dark:bg-slate-950/80">
					<div>
						<h2 className="font-bold tracking-tight">
							{t("header.title")}
						</h2>
						<p className="text-muted-foreground mt-0.5 text-sm">
							{t("header.description")}
						</p>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<span
							className={cn(
								"inline-flex min-h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold",
								isSetup
									? "border-cyan-200 bg-cyan-50 text-cyan-950 dark:border-cyan-900 dark:bg-cyan-950/45 dark:text-cyan-100"
									: "border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100",
							)}
						>
							{isSetup ? (
								<ShieldCheck className="size-3.5" aria-hidden="true" />
							) : (
								<Disc className="size-3.5" aria-hidden="true" />
							)}
							{isSetup
								? t("header.noAccess")
								: isRecording || isSaving
									? t("header.active")
									: t("header.complete")}
						</span>
						<span
							className={cn(
								"inline-flex min-h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold",
								browserState === "supported"
									? "border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100"
									: browserState === "unsupported"
										? "border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/35 dark:text-amber-100"
										: "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300",
							)}
							role="status"
						>
							{browserState === "supported" ? (
								<Check className="size-3.5" aria-hidden="true" />
							) : (
								<CircleAlert className="size-3.5" aria-hidden="true" />
							)}
							{browserState === "checking"
								? t("header.checking")
								: browserState === "supported"
									? t("header.sharingAvailable")
									: t("header.sharingUnavailable")}
						</span>
					</div>
				</div>

				<div className="grid gap-5 p-4 sm:p-6 lg:grid-cols-[minmax(0,1.08fr)_minmax(22rem,0.92fr)] lg:p-8">
					<div className="min-w-0 rounded-3xl border border-blue-100 bg-white/88 p-4 shadow-sm sm:p-5 dark:border-blue-950 dark:bg-slate-900/82">
						<div className="flex items-center justify-between gap-3">
							<div>
								<p className="font-bold">{t("preview.title")}</p>
								<p className="text-muted-foreground mt-0.5 text-xs">
									{isSuccess
										? t("preview.completed")
										: isRecording
											? t("preview.recording")
											: isSaving
												? t("preview.saving")
												: isFailed
													? t("preview.failed")
													: t("preview.empty")}
								</p>
							</div>
							<span
								className={cn(
									"rounded-full border px-3 py-1.5 text-xs font-semibold",
									isSuccess
										? "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/45 dark:text-emerald-100"
										: isRecording || isSaving
											? "border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/45 dark:text-red-100"
											: isFailed
												? "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/35 dark:text-amber-100"
												: "border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950/45 dark:text-blue-100",
								)}
							>
								{isSuccess
									? t("preview.statusComplete")
									: isRecording
										? recordingState.status === "paused"
											? t("preview.statusPaused")
											: t("preview.statusRecording")
										: isSaving
											? t("preview.statusSaving")
											: isFailed
												? t("preview.statusFailed")
												: t("preview.statusSetup")}
							</span>
						</div>

						<div className="relative mt-4 flex aspect-video min-h-64 items-center justify-center overflow-hidden rounded-2xl border border-slate-800 bg-[radial-gradient(circle_at_50%_10%,rgba(14,165,233,0.22),transparent_34%),linear-gradient(145deg,#0f172a,#020617)] p-6 text-white shadow-inner sm:min-h-80">
							<div className="absolute inset-0 opacity-35 [background-image:linear-gradient(rgba(255,255,255,.055)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.055)_1px,transparent_1px)] [background-size:32px_32px]" />

							{isSuccess && recordingState.recordedUrl ? (
								<video
									src={recordingState.recordedUrl}
									controls
									className="relative z-10 max-h-full max-w-full rounded-lg"
									autoPlay
									aria-label={t("preview.videoLabel")}
								>
									<track kind="captions" />
								</video>
							) : isRecording || isSaving ? (
								<div className="relative z-10 text-center">
									{recordingState.status === "countdown" ? (
										<>
											<div className="text-6xl font-bold tracking-tighter">
												{countdownValue || t("preview.ready")}
											</div>
											<p className="mt-4 text-sm text-slate-300">
												{t("preview.startsIn")}
											</p>
										</>
									) : (
										<>
											<div className="flex items-center justify-center gap-6">
												<div
													className={cn(
														"size-4 rounded-full",
														recordingState.status === "recording"
															? "bg-red-500 animate-pulse"
															: "bg-amber-500",
													)}
													aria-hidden="true"
												/>
												<div className="text-4xl font-mono font-bold tracking-tight">
													{formatDuration(recordingState.duration)}
												</div>
											</div>
											<p className="mt-4 text-sm text-slate-300">
												{recordingState.status === "recording"
													? t("preview.inProgress")
													: recordingState.status === "paused"
														? t("preview.paused")
														: t("preview.savingProgress")}
											</p>
										</>
									)}
								</div>
							) : isFailed ? (
								<div className="relative z-10 max-w-md text-center">
									<span className="mx-auto flex size-16 items-center justify-center rounded-2xl border border-white/10 bg-white/8 shadow-xl backdrop-blur-sm">
										<CircleAlert
											className="size-7 text-red-300"
											aria-hidden="true"
										/>
									</span>
									<p className="mt-5 text-xl font-bold tracking-tight">
										{t("preview.failedTitle")}
									</p>
									<p className="mt-2 text-sm leading-6 text-slate-300">
										{recordingError}
									</p>
								</div>
							) : (
								<div className="relative z-10 max-w-md text-center">
									<span className="mx-auto flex size-16 items-center justify-center rounded-2xl border border-white/10 bg-white/8 shadow-xl backdrop-blur-sm">
										{hasScreen ? (
											<MonitorUp
												className="size-7 text-cyan-300"
												aria-hidden="true"
											/>
										) : (
											<Camera
												className="size-7 text-cyan-300"
												aria-hidden="true"
											/>
										)}
									</span>
									<p className="mt-5 text-xl font-bold tracking-tight">
										{hasScreen
											? t("preview.chooseScreen")
											: t("preview.cameraOnly")}
									</p>
									<p className="mt-2 text-sm leading-6 text-slate-300">
										{hasScreen
											? t("preview.chooseScreenDescription")
											: t("preview.cameraOnlyDescription")}
									</p>
								</div>
							)}

							{hasCamera && layout === "screen-camera" && !isSuccess ? (
								<div
									className={cn(
										"absolute flex size-20 items-center justify-center rounded-full border-4 border-slate-950 bg-gradient-to-br from-blue-500 to-cyan-300 text-slate-950 shadow-2xl sm:size-24",
										cameraPosition === "top-left" && "top-5 left-5",
										cameraPosition === "top-right" && "top-5 right-5",
										cameraPosition === "bottom-left" && "bottom-5 left-5",
										cameraPosition === "bottom-right" && "right-5 bottom-5",
									)}
									aria-label={t("preview.cameraPosition", {
										position: t(
											CAMERA_POSITIONS.find(
												(position) => position.value === cameraPosition,
											)?.labelKey ?? "camera.bottomRight",
										),
									})}
								>
									<Camera className="size-7" aria-hidden="true" />
								</div>
							) : null}
						</div>

						<div className="mt-4 grid gap-2 text-xs sm:grid-cols-3">
							<div className="flex min-h-11 items-center gap-2 rounded-xl bg-slate-100 px-3 font-semibold text-slate-700 dark:bg-slate-950 dark:text-slate-200">
								{isRecording && recordingState.status === "recording" ? (
									<Disc
										className="size-4 text-red-600 dark:text-red-300 animate-spin"
										aria-hidden="true"
									/>
								) : hasScreen ? (
									<MonitorUp
										className="size-4 text-blue-600 dark:text-cyan-300"
										aria-hidden="true"
									/>
								) : (
									<MonitorUp className="size-4" aria-hidden="true" />
								)}
								{isRecording
									? t("summary.recordingActive")
									: hasScreen
										? t("summary.screenRequested")
										: t("summary.noScreen")}
							</div>
							<div className="flex min-h-11 items-center gap-2 rounded-xl bg-slate-100 px-3 font-semibold text-slate-700 dark:bg-slate-950 dark:text-slate-200">
								{audioActivity.microphone && isRecording ? (
									<Mic
										className="size-4 text-green-600 dark:text-green-300 animate-pulse"
										aria-hidden="true"
									/>
								) : microphone ? (
									<Mic
										className="size-4 text-blue-600 dark:text-cyan-300"
										aria-hidden="true"
									/>
								) : (
									<VolumeX className="size-4" aria-hidden="true" />
								)}
								{audioActivity.microphone && isRecording
									? t("summary.micActive")
									: microphone
										? t("summary.micRequested")
										: t("summary.micOff")}
							</div>
							<div className="flex min-h-11 items-center gap-2 rounded-xl bg-slate-100 px-3 font-semibold text-slate-700 dark:bg-slate-950 dark:text-slate-200">
								<Clock3
									className="size-4 text-blue-600 dark:text-cyan-300"
									aria-hidden="true"
								/>
								{countdown === "off"
									? t("summary.noCountdown")
									: t("summary.countdown", { seconds: countdown })}
							</div>
						</div>
					</div>

					<div className="min-w-0 space-y-4">
						<div
							className={cn(
								"rounded-2xl border border-blue-100 bg-white/90 p-5 shadow-sm dark:border-blue-950 dark:bg-slate-900/85",
								!isSetup && "opacity-50 pointer-events-none",
							)}
						>
							<p className="font-bold">{t("layout.title")}</p>
							<div
								className="mt-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3"
								role="group"
								aria-label={t("layout.ariaLabel")}
							>
								{RECORDING_LAYOUTS.map((item) => {
									const Icon = item.icon;
									return (
										<OptionButton
											key={item.value}
											value={item.value}
											selectedValue={layout}
											onSelect={selectLayout}
											className="flex min-h-20 flex-col items-center justify-center gap-2 px-2 text-center"
										>
											<Icon className="size-5" aria-hidden="true" />
											<span>{t(item.labelKey)}</span>
										</OptionButton>
									);
								})}
							</div>
						</div>

						<div
							className={cn(
								"rounded-2xl border border-blue-100 bg-white/90 p-5 shadow-sm dark:border-blue-950 dark:bg-slate-900/85",
								!isSetup && "opacity-50 pointer-events-none",
							)}
						>
							<p className="font-bold">{t("audio.title")}</p>
							<div className="mt-3 divide-y rounded-xl border border-slate-200 bg-slate-50/80 px-3 dark:border-slate-800 dark:bg-slate-950/65">
								<div className="flex min-h-14 items-center justify-between gap-4 py-2">
									<label
										htmlFor="screen-recorder-microphone"
										className={cn(
											"flex items-center gap-3 text-sm font-semibold",
											!isSetup && "cursor-pointer",
											!isSetup && "cursor-not-allowed opacity-50",
										)}
									>
										<Mic
											className="size-4 text-blue-600 dark:text-cyan-300"
											aria-hidden="true"
										/>
										{t("audio.microphone")}
									</label>
									<Switch
										id="screen-recorder-microphone"
										checked={microphone}
										disabled={!isSetup}
										onCheckedChange={(checked) => {
											setMicrophone(checked);
										}}
									/>
								</div>
								<div className="flex min-h-14 items-center justify-between gap-4 py-2">
									<div>
										<label
											htmlFor="screen-recorder-system-audio"
											className={cn(
												"flex items-center gap-3 text-sm font-semibold",
												hasScreen && isSetup
													? "cursor-pointer"
													: "text-muted-foreground cursor-not-allowed",
											)}
										>
											<Volume2
												className="size-4 text-blue-600 dark:text-cyan-300"
												aria-hidden="true"
											/>
											{t("audio.system")}
										</label>
										{hasScreen ? (
											<p className="text-muted-foreground mt-1 pl-7 text-xs">
												{t("audio.shareHint")}
											</p>
										) : null}
									</div>
									<Switch
										id="screen-recorder-system-audio"
										checked={systemAudio}
										disabled={!hasScreen || !isSetup}
										onCheckedChange={(checked) => {
											setSystemAudio(checked);
										}}
									/>
								</div>
							</div>
						</div>

						{layout === "screen-camera" ? (
							<div
								className={cn(
									"rounded-2xl border border-blue-100 bg-white/90 p-5 shadow-sm dark:border-blue-950 dark:bg-slate-900/85",
									!isSetup && "opacity-50 pointer-events-none",
								)}
							>
								<p className="font-bold">{t("camera.title")}</p>
								<div
									className="mt-4 grid grid-cols-4 gap-2"
									role="group"
									aria-label={t("camera.ariaLabel")}
								>
									{CAMERA_POSITIONS.map((position) => (
										<OptionButton
											key={position.value}
											value={position.value}
											selectedValue={cameraPosition}
											onSelect={(value) => {
												setCameraPosition(value);
											}}
											className="flex min-h-12 items-center justify-center p-2"
										>
											<span className="sr-only">{t(position.labelKey)}</span>
											<span
												className={cn(
													"relative block size-6 rounded-md border border-current/30",
													"after:absolute after:size-2 after:rounded-full after:bg-current",
													position.value === "top-left" &&
														"after:top-1 after:left-1",
													position.value === "top-right" &&
														"after:top-1 after:right-1",
													position.value === "bottom-left" &&
														"after:bottom-1 after:left-1",
													position.value === "bottom-right" &&
														"after:right-1 after:bottom-1",
												)}
											/>
										</OptionButton>
									))}
								</div>
							</div>
						) : null}

						<details
							className={cn(
								"group rounded-2xl border border-blue-100 bg-white/90 px-5 py-3 shadow-sm dark:border-blue-950 dark:bg-slate-900/85",
								!isSetup && "opacity-50 pointer-events-none",
							)}
						>
							<summary className="flex min-h-11 cursor-pointer list-none items-center justify-between rounded-lg font-bold outline-none focus-visible:ring-2 focus-visible:ring-ring">
								{t("advanced.title")}
								<ChevronDown
									className="size-4 transition-transform group-open:rotate-180 motion-reduce:transition-none"
									aria-hidden="true"
								/>
							</summary>
							<div className="grid gap-4 border-t pt-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
								<fieldset>
									<legend className="text-sm font-semibold">
										{t("advanced.quality")}
									</legend>
									<div className="mt-2 grid grid-cols-2 gap-2">
										{(["720p", "1080p"] as const).map((option) => (
											<OptionButton
												key={option}
												value={option}
												selectedValue={quality}
												onSelect={(value) => {
													setQuality(value);
												}}
											>
												{option}
											</OptionButton>
										))}
									</div>
								</fieldset>
								<fieldset>
									<legend className="text-sm font-semibold">
										{t("advanced.countdown")}
									</legend>
									<div className="mt-2 grid grid-cols-3 gap-2">
										{(["off", "3", "5"] as const).map((option) => (
											<OptionButton
												key={option}
												value={option}
												selectedValue={countdown}
												onSelect={(value) => {
													setCountdown(value);
												}}
												className="px-2"
											>
												{option === "off" ? t("advanced.off") : `${option}s`}
											</OptionButton>
										))}
									</div>
								</fieldset>
							</div>
						</details>

						{/* Recording controls */}
						{isRecording ? (
							<div className="flex gap-2">
								{recordingState.status === "paused" ? (
									<Button
										className="h-12 flex-1 rounded-xl bg-emerald-600 text-base text-white shadow-lg shadow-emerald-600/20 hover:bg-emerald-500"
										onClick={handleResumeRecording}
										aria-label={t("controls.resumeAria")}
									>
										<Play className="size-4.5" aria-hidden="true" />
										{t("controls.resume")}
									</Button>
								) : (
									<Button
										className="h-12 flex-1 rounded-xl bg-amber-600 text-base text-white shadow-lg shadow-amber-600/20 hover:bg-amber-500"
										onClick={handlePauseRecording}
										aria-label={t("controls.pauseAria")}
									>
										<Pause className="size-4.5" aria-hidden="true" />
										{t("controls.pause")}
									</Button>
								)}
								<Button
									className="h-12 rounded-xl bg-red-600 px-4 text-base text-white shadow-lg shadow-red-600/20 hover:bg-red-500"
									onClick={handleStopRecording}
									aria-label={t("controls.stopAria")}
								>
									<StopCircle className="size-4.5" aria-hidden="true" />
								</Button>
								<Button
									className="h-12 rounded-xl bg-slate-600 px-4 text-base text-white shadow-lg hover:bg-slate-500"
									onClick={handleCancelRecording}
									aria-label={t("controls.cancelAria")}
								>
									<X className="size-4.5" aria-hidden="true" />
								</Button>
							</div>
						) : isSuccess ? (
							<div className="flex gap-2">
								<Button
									className="h-12 flex-1 rounded-xl bg-emerald-600 text-base text-white shadow-lg shadow-emerald-600/20 hover:bg-emerald-500"
									onClick={handleDownloadRecording}
									aria-label={t("controls.downloadAria")}
								>
									<Download className="size-4.5" aria-hidden="true" />
									{t("controls.download")}
								</Button>
								<Button
									className="h-12 flex-1 rounded-xl bg-blue-600 text-base text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500"
									onClick={handleResetRecording}
									aria-label={t("controls.newAria")}
								>
									<MonitorPlay className="size-4.5" aria-hidden="true" />
									{t("controls.new")}
								</Button>
							</div>
						) : isSaving ? (
							<div className="flex gap-2">
								<Button
									className="h-12 w-full rounded-xl bg-slate-600 text-base text-white shadow-lg"
									disabled
									aria-label={t("controls.savingAria")}
								>
									<Disc className="size-4.5 animate-spin" aria-hidden="true" />
									{t("controls.saving")}
								</Button>
							</div>
						) : isFailed ? (
							<div className="flex gap-2">
								<Button
									className="h-12 w-full rounded-xl bg-blue-600 text-base text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500"
									onClick={handleResetRecording}
									aria-label={t("controls.tryAgainAria")}
								>
									{t("controls.tryAgain")}
								</Button>
							</div>
						) : (
							<Button
								className="h-12 w-full rounded-xl bg-blue-600 text-base text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500 disabled:bg-slate-400 disabled:text-white dark:disabled:bg-slate-700"
								disabled={!canStart || browserState === "checking"}
								onClick={handleStartRecording}
								aria-describedby="screen-recorder-action-status"
							>
								<MonitorUp className="size-4.5" aria-hidden="true" />
								{t("controls.start")}
							</Button>
						)}

						<div
							id="screen-recorder-action-status"
							className={cn(
								"rounded-2xl border p-4 text-sm leading-6",
								isSuccess
									? "border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/35 dark:text-emerald-100"
									: isFailed
										? "border-red-200 bg-red-50 text-red-950 dark:border-red-900 dark:bg-red-950/35 dark:text-red-100"
										: isRecording || isSaving
											? "border-blue-200 bg-blue-50 text-blue-950 dark:border-cyan-900 dark:bg-cyan-950/35 dark:text-cyan-50"
											: !canStart && browserState !== "checking"
												? "border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
												: "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-800 dark:bg-slate-950/70 dark:text-slate-300",
							)}
							role="status"
							aria-live="polite"
						>
							{isSuccess ? (
								<div className="flex items-start gap-3">
									<Check
										className="mt-0.5 size-4.5 shrink-0"
										aria-hidden="true"
									/>
									<div>
										<p>
											<strong className="font-bold">
												{t("status.successTitle")}
											</strong>{" "}
											{t("status.successDescription")}
										</p>
										{recordingState.recordedBlob && (
											<p className="mt-2 text-xs">
												{t("status.fileInfo", {
													size: getFileInfo(
														recordingState.recordedBlob,
														locale,
													).size,
													duration: formatDuration(recordingState.duration),
												})}
											</p>
										)}
									</div>
								</div>
							) : isFailed ? (
								<div className="flex items-start gap-3">
									<CircleAlert
										className="mt-0.5 size-4.5 shrink-0"
										aria-hidden="true"
									/>
									<p>
										<strong className="font-bold">
											{t("status.failedTitle")}
										</strong>{" "}
										{recordingError}
									</p>
								</div>
							) : isRecording ? (
								<div className="flex items-start gap-3">
									<Disc
										className="mt-0.5 size-4.5 shrink-0 animate-spin"
										aria-hidden="true"
									/>
									<p>
										<strong className="font-bold">
											{recordingState.status === "paused"
												? t("status.paused")
												: recordingState.status === "countdown"
													? t("status.starting")
													: t("status.inProgress")}
										</strong>{" "}
										{recordingState.status === "recording"
											? t("status.duration", {
													duration: formatDuration(recordingState.duration),
												})
											: t("status.active")}
									</p>
								</div>
							) : isSaving ? (
								<div className="flex items-start gap-3">
									<Disc
										className="mt-0.5 size-4.5 shrink-0 animate-spin"
										aria-hidden="true"
									/>
									<p>
										<strong className="font-bold">
											{t("status.savingTitle")}
										</strong>{" "}
										{t("status.savingDescription")}
									</p>
								</div>
							) : !canStart && browserState !== "checking" ? (
								<div className="flex items-start gap-3">
									<CircleAlert
										className="mt-0.5 size-4.5 shrink-0"
										aria-hidden="true"
									/>
									<p>
										{t("status.unsupported")}
									</p>
								</div>
							) : (
								<div className="flex items-start gap-3">
									<ShieldCheck
										className="mt-0.5 size-4.5 shrink-0 text-cyan-600 dark:text-cyan-300"
										aria-hidden="true"
									/>
									<p>
										{t("status.ready")}
									</p>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
